import type { ReactNode } from 'react'
import { cn } from '../lib/cn'
import { toPersianDigits } from '../lib/date'

/* ------------------------------------------------------------------
   Status Chip — سند مادر بند ۱۱.۱
   ------------------------------------------------------------------
   نکته مهم: طبق بند ۱۰، هر ماژول State Machine خودش را دارد و
   وضعیت‌ها عمداً یکسان نیستند. این کامپوننت فقط «لحن» را استاندارد
   می‌کند، نه مجموعه وضعیت‌ها را.
   ------------------------------------------------------------------ */

export type StatusTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger' | 'brand'

const TONES: Record<StatusTone, string> = {
  neutral:
    'bg-[var(--pf-surface-muted)] text-[var(--pf-text-muted)] ring-[var(--pf-border)]',
  info:
    'bg-[var(--pf-info-50)] text-[var(--pf-info-700)] ring-[var(--pf-info-100)]',
  success:
    'bg-[var(--pf-success-50)] text-[var(--pf-success-700)] ring-[var(--pf-success-100)]',
  warning:
    'bg-[var(--pf-warning-50)] text-[var(--pf-warning-700)] ring-[var(--pf-warning-100)]',
  danger:
    'bg-[var(--pf-danger-50)] text-[var(--pf-danger-700)] ring-[var(--pf-danger-100)]',
  brand:
    'bg-[var(--pf-brand-50)] text-[var(--pf-brand-700)] ring-[var(--pf-brand-100)]',
}

export interface StatusChipProps {
  children: ReactNode
  tone?: StatusTone
  /** نقطه رنگی ابتدای چیپ */
  dot?: boolean
  size?: 'sm' | 'md'
  className?: string
}

export function StatusChip({
  children,
  tone = 'neutral',
  dot = true,
  size = 'md',
  className,
}: StatusChipProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-[var(--pf-radius-full)]',
        'ring-1 ring-inset font-[var(--pf-weight-medium)] whitespace-nowrap',
        size === 'sm'
          ? 'px-2 py-0.5 text-[length:var(--pf-text-2xs)]'
          : 'px-2.5 py-1 text-[length:var(--pf-text-xs)]',
        TONES[tone],
        className,
      )}
    >
      {dot && <span className="size-1.5 rounded-full bg-current opacity-70" aria-hidden="true" />}
      {children}
    </span>
  )
}

/* ------------------------------------------------------------------
   نشان محرمانگی — ۴ سطح مصوب کارفرما (پاسخ ۸ بند ۲۸)
   عادی · اختصاصی · محرمانه · خیلی محرمانه
   ------------------------------------------------------------------ */

export type ConfidentialityLevel =
  | 'normal'
  | 'restricted'
  | 'confidential'
  | 'highly_confidential'

const CONFIDENTIALITY: Record<
  ConfidentialityLevel,
  { label: string; className: string }
> = {
  normal: {
    label: 'عادی',
    className: 'bg-[var(--pf-conf-normal-bg)] text-[var(--pf-conf-normal)]',
  },
  restricted: {
    label: 'اختصاصی',
    className: 'bg-[var(--pf-conf-restricted-bg)] text-[var(--pf-conf-restricted)]',
  },
  confidential: {
    label: 'محرمانه',
    className: 'bg-[var(--pf-conf-confidential-bg)] text-[var(--pf-conf-confidential)]',
  },
  highly_confidential: {
    label: 'خیلی محرمانه',
    className: 'bg-[var(--pf-conf-highly-bg)] text-[var(--pf-conf-highly)]',
  },
}

export interface ConfidentialityBadgeProps {
  level: ConfidentialityLevel
  className?: string
}

export function ConfidentialityBadge({ level, className }: ConfidentialityBadgeProps) {
  const config = CONFIDENTIALITY[level] ?? CONFIDENTIALITY.normal
  const isSensitive = level === 'confidential' || level === 'highly_confidential'

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-[var(--pf-radius-sm)] px-2 py-0.5',
        'text-[length:var(--pf-text-2xs)] font-[var(--pf-weight-semibold)] whitespace-nowrap',
        config.className,
        className,
      )}
      title={`سطح محرمانگی: ${config.label}`}
    >
      {isSensitive && (
        <svg viewBox="0 0 16 16" fill="none" className="size-3" aria-hidden="true">
          <path
            d="M4.5 7V5a3.5 3.5 0 1 1 7 0v2M3.5 7h9v6h-9z"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
      {config.label}
    </span>
  )
}

/* ------------------------------------------------------------------
   Badge شمارشی — برای Sidebar و Inbox
   ------------------------------------------------------------------ */

export interface CountBadgeProps {
  count: number
  max?: number
  /** `nav-danger` نسخه روشن‌تر قرمز، مخصوص نشستن روی ناوبری تیره */
  tone?: 'brand' | 'danger' | 'neutral' | 'nav-danger'
  className?: string
}

export function CountBadge({ count, max = 99, tone = 'brand', className }: CountBadgeProps) {
  if (count <= 0) return null

  const tones = {
    /* رنگ متن از --pf-on-primary می‌آید تا با هر دو پوسته هماهنگ بماند */
    brand: 'bg-[var(--pf-primary)] text-[var(--pf-on-primary)]',
    danger: 'bg-[var(--pf-danger-600)] text-white',
    /* روی سطح تیره ناوبری، قرمز اصلی دیده نمی‌شود (۲٫۷۳) */
    'nav-danger': 'bg-[var(--pf-nav-danger)] text-white',
    neutral: 'bg-[var(--pf-surface-muted)] text-[var(--pf-text-muted)]',
  }

  return (
    <span
      className={cn(
        'inline-flex min-w-5 h-5 items-center justify-center rounded-[var(--pf-radius-full)]',
        'px-1.5 text-[length:var(--pf-text-2xs)] font-[var(--pf-weight-semibold)] pf-tnum',
        tones[tone],
        className,
      )}
    >
      {count > max ? `${toPersianDigits(max)}+` : toPersianDigits(count)}
    </span>
  )
}
